import React, { useEffect } from "react";
import { useState, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { MDBCol, MDBRow } from "mdbreact";

import "./ManageAccount.scss";

import { AbsDropDown, Button, Input, Label } from "abs-ui-components";

import CustomTable from "../../common/customTable/CustomTable";
import { countryList, MANAGE_ACCOUNT_DATA } from "./constants";
import { emitToasterMsg } from "../../services/toasterUtility";
import { updateAccountDetails } from "../../services/ManageAccountService";
import BreadCrumb from "../../common/breadCrumb/BreadCrumb";

import {
	changeOfficeSign,
	changePassword,
	getUserDetails,
	getWCNsDetails,
	getOfficeSign,
} from "../../services/AdminService";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";

const AdminInput = ({
	name,
	control,
	defaultValue,
	rules,
	placeholder,
	type,
	errors,
	setError,
	clearErrors,
}) => {
	// Define a validation function
	const validateField = (value) => {
		// You can add more validation rules here
		if (rules?.required && !value) {
			setError(name, {
				type: "manual",
				message: rules.required,
			});
		} else {
			clearErrors(name);
		}
	};

	return (
		<Controller
			name={name}
			control={control}
			rules={rules}
			render={({ field }) => (
				<>
					<Input
						required
						id={name}
						key={defaultValue}
						onChange={(e) => {
							field.onChange(e);
							validateField(e);
						}}
						type={type}
						value={field.value}
						defaultValue={defaultValue}
						autoFocus={false}
						className="input-details mt-2"
						placeholder={placeholder}
					/>
					{errors[name] && (
						<p className="error">{errors[name].message}</p>
					)}
				</>
			)}
		/>
	);
};

const ManageAccountPage = () => {
	const [selectedCountry, setSelectedCountry] = useState(
		localStorage.getItem("selectedCountry")
	);
	const [loading, setLoading] = useState(false);

	const handleCountryChange = (selectedOption) => {
		localStorage.setItem("selectedCountry", selectedOption.value);
		setSelectedCountry(selectedOption.value);
	};

	const {
		control,
		handleSubmit: handleSubmitNumber,
		formState: { numbererrors },
		getValues: getValuesNumber,
		setError: setErrorNumber,
		clearErrors: clearErrorsNumber,
	} = useForm();

	const {
		control: controlPasword,
		formState: { errors },
		handleSubmit: handlePaswordSubmit,
		getValues,
		setError,
		clearErrors,
		watch,
	} = useForm();

	const {
		control: controlSign,
		getValues: getValuesSign,
		handleSubmit: handleSignSubmit,
		setError: setErrorSign,
		clearErrors: clearErrorsSign,
		formState: { errors: signErrors },
	} = useForm();

	const [assignedWCNList, setassignedWCNList] = useState([]);
	const [assignedRoleList, setassignedRoleList] = useState([]);
	const manageAccountColumns = useMemo(
		() => MANAGE_ACCOUNT_DATA.manage_account_columns,
		[]
	);
	const tableHeader = React.useMemo(
		() => manageAccountColumns,
		[manageAccountColumns]
	);
	const user = useSelector((state) => state.userInfo.userData) || null;
	const { email, userName, phoneNumber, officeLocation } = user || {};

	const breadcrumb = useMemo(
		() => [
			{
				label: MANAGE_ACCOUNT_DATA["nav_account_text"],
				url: "",
				navId: "account_navId",
			},
			{
				label: MANAGE_ACCOUNT_DATA["nav_manageaccount_text"],
				url: "",
				navId: "manageaccount_navId",
			},
		],
		[]
	);

	useEffect(() => {
		getUserDetailsByUserId();
		fetchAssignedWcnDetails();
	}, []);

	const getUserDetailsByUserId = async () => {
		setLoading(true);
		try {
			const response = await getUserDetails(email);
			if (response) {
				setassignedRoleList(response.roles);
				setLoading(false);
			}
		} catch (error) {
			setLoading(false);
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const fetchAssignedWcnDetails = async () => {
		let requestPayload = {
			pagination: {
				page: 1,
				pageSize: 1000,
			},
			globalFilter: "",
			filters: [],
			sortOption: {
				sortField: "",
				isAscending: false,
			},
		};
		try {
			const response = await getWCNsDetails(requestPayload);
			if (response && response?.Data) {
				transformWCNData(response.Data);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const transformWCNData = (data) => {
		let WCNData = [];
		data.forEach((element) => {
			element.IsDcs = element.IsDcs === false ? "No" : "Yes";
			element.IsEumrv = element.IsEumrv === false ? "No" : "Yes";
			element.IsUkmrv = element.IsUkmrv === false ? "No" : "Yes";
			WCNData.push(element);
		});
		setassignedWCNList(WCNData);
	};

	const handleForm = async () => {
		const values = getValuesNumber() || {};
		const { phoneNumber } = values;
		const numberErrors = {};

		// Validate phoneNumber
		if (!phoneNumber) {
			numberErrors.phoneNumber = "Phone Number is required";
		} else if (!/^\d+$/.test(phoneNumber)) {
			numberErrors.phoneNumber = "Phone Number must be a number";
		}

		if (Object.keys(numberErrors).length > 0) {
			Object.keys(numberErrors).forEach((field) => {
				setErrorNumber(field, {
					type: "manual",
					message: numberErrors[field],
				});
			});

			return;
		}

		clearErrorsNumber();
		try {
			const response = await updateAccountDetails(userName, phoneNumber);
			if (response && response.isSuccess) {
				emitToasterMsg("success", "Updated successfully");
			}
		} catch (error) {
			console.error(error);
		}
	};

	const newPassword = watch("newPassword");
	const confirmNewPassword = watch("confirmNewPassword");

	const handleUpdateDetails = async () => {
		const values = getValues();
		const { newPassword, confirmNewPassword } = values;
		const errors = {};

		// Validate new password
		if (!newPassword) {
			errors.newPassword = "New Password is required";
		}

		if (!confirmNewPassword) {
			errors.confirmNewPassword = "Confirm Password is required";
		} else if (confirmNewPassword !== newPassword) {
			errors.confirmNewPassword = "Passwords must match";
		}
		if (Object.keys(errors).length > 0) {
			Object.keys(errors).forEach((field) => {
				setError(field, {
					type: "manual",
					message: errors[field],
				});
			});
			return;
		}

		clearErrors();

		const requestPayload = {
			UserName: userName,
			newPassword: newPassword,
		};

		try {
			// Make the API call to change password
			const response = await changePassword(requestPayload);
			if (response?.isSuccess) {
				emitToasterMsg("success", "Password changed successfully!");
			} else {
				emitToasterMsg(
					"error",
					response?.errorMessage || "An error occurred"
				);
			}
		} catch (error) {
			console.error(error);
			emitToasterMsg("error", "An error occurred");
		}
	};

	const handleSignDetails = async () => {
		const values = getValuesSign();
		const { officeSign } = values;
		const signErrors = {};

		// Validate office sign
		if (!officeSign) {
			signErrors.officeSign = "Location is required";
		}

		if (Object.keys(signErrors).length > 0) {
			Object.keys(signErrors).forEach((field) => {
				setErrorSign(field, {
					type: "manual",
					message: signErrors[field],
				});
			});
			return;
		}
		clearErrorsSign();

		const requestPayload = {
			OfficeSign: officeSign,
		};

		try {
			const response = await changeOfficeSign(requestPayload);
			if (response && response?.isSuccess) {
				emitToasterMsg("success", "Location set successfully!");
			} else {
				emitToasterMsg("error", response.errorMessage);
			}
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<>
			<div className="ml-4 mt-2">
				<BreadCrumb items={breadcrumb} />
			</div>
			<div className="manage-container">
				<MDBRow>
					{/* First Half */}
					<MDBCol className="col-6 ml-3 mt-2">
						<div className="account-details">
							<h4 className="header-text">Account Details</h4>

							<form
								onSubmit={handleSubmitNumber(handleForm)}
								id="manage-account-form-id"
							>
								<MDBRow>
									<MDBCol className="col-4 country-dropdown-container">
										<Label
											className="label-text"
											name="Select Country"
										/>

										<div className="mt-2">
											<Controller
												name="country"
												control={control}
												defaultValue=""
												rules={{
													required:
														"Country is required",
												}}
												render={({ field }) => (
													<div>
														<AbsDropDown
															name="country"
															options={
																countryList
																	? countryList
																	: []
															}
															selected={
																selectedCountry
															}
															handleChange={
																handleCountryChange
															}
															className="country-dropdown"
														/>
														{errors.country && (
															<p className="error">
																{
																	errors
																		.country
																		.message
																}
															</p>
														)}
													</div>
												)}
											/>
										</div>
									</MDBCol>

									<MDBCol className="col-4">
										<Label
											className="label-text"
											name={
												MANAGE_ACCOUNT_DATA.label_phonenumber
											}
										/>
										<Controller
											name="phoneNumber"
											control={control}
											defaultValue={phoneNumber}
											rules={{
												required:
													"Phone Number is required",
												pattern: {
													value: /^\d*$/,
													message:
														"Phone Number must be a valid number",
												},
											}}
											render={({ field, fieldState }) => (
												<div>
													<Input
														id="manage_account_phone"
														{...field}
														className="input-details fuel-input mt-2"
														placeholder="Phone Number"
														type="text"
														autoFocus={false}
														onChange={(e) => {
															const value = e;
															if (
																value.length <=
																	20 &&
																/^\d*$/.test(
																	value
																)
															) {
																field.onChange(
																	value
																);
																clearErrorsNumber(
																	"phoneNumber"
																);
															} else if (
																value.length >
																20
															) {
																setErrorNumber(
																	"phoneNumber",
																	{
																		type: "manual",
																		message:
																			"Max length is 20 characters",
																	}
																);
															} else {
																setErrorNumber(
																	"phoneNumber",
																	{
																		type: "manual",
																		message:
																			"Phone Number must be a number",
																	}
																);
															}
														}}
													/>
													{fieldState.error && (
														<p className="error">
															{
																fieldState.error
																	.message
															}
														</p>
													)}
												</div>
											)}
										/>
									</MDBCol>
									<MDBCol className="col-2">
										<Button
											id="manage-account-update"
											onClick={handleForm}
											className="update-button"
										>
											{
												MANAGE_ACCOUNT_DATA.update_btn_text
											}
										</Button>
									</MDBCol>
								</MDBRow>
							</form>

							<form
								onSubmit={handlePaswordSubmit(
									handleUpdateDetails
								)}
							>
								<MDBRow className="mt-5">
									<MDBCol className="col-4">
										<Label
											bold
											required
											name="New Password"
											className="mb-1"
										/>
										<AdminInput
											name="newPassword"
											control={controlPasword}
											defaultValue=""
											placeholder="New Password"
											type="password"
											errors={errors}
											setError={setError}
											clearErrors={clearErrors}
										/>
									</MDBCol>
									<MDBCol className="col-4">
										<Label
											bold
											required
											name="Confirm New Password"
											className="mt-1"
										/>
										<AdminInput
											name="confirmNewPassword"
											control={controlPasword}
											defaultValue=""
											placeholder="Confirm New Password"
											type="password"
											errors={errors}
											setError={setError}
											clearErrors={clearErrors}
										/>
									</MDBCol>
									<MDBCol className="col-3 mt-2">
										<div className="d-flex password-container">
											<Button
												type="submit"
												onClick={handleUpdateDetails}
												className="change-password-manage text-center mt-4"
											>
												Change Password
											</Button>
										</div>
									</MDBCol>
								</MDBRow>
							</form>

							<form
								onSubmit={handleSignSubmit(handleSignDetails)}
							>
								<MDBRow className="mt-5">
									<MDBCol className="col-8">
										<Label
											bold
											required
											name="Location"
											className="mb-1"
										/>
										<AdminInput
											name="officeSign"
											control={controlSign}
											errors={signErrors}
											defaultValue={officeLocation}
											rules={{
												required:
													"Location is required",
											}}
											placeholder="Location"
											type="text"
											setError={setErrorSign}
											clearErrors={clearErrorsSign}
										/>
									</MDBCol>
									<MDBCol className="col-3 mt-2">
										<div className="d-flex password-container">
											<Button
												type="submit"
												onClick={handleSignDetails}
												className="change-password-manage text-center mt-4"
											>
												Set Location
											</Button>
										</div>
									</MDBCol>
								</MDBRow>
							</form>
						</div>
					</MDBCol>

					{/* Second Half */}
					<MDBCol className="col-5">
						<div className="manage-container ml-4 mt-4">
							<h4 className="header-text mt-2">
								{MANAGE_ACCOUNT_DATA.associated_roles}
							</h4>
							<div className="mt-4">
								<div className="card">
									<div className="card-body">
										<div className="row col-lg-12">
											{loading ? (
												<CircularProgress size={20} />
											) : (
												assignedRoleList.join(", ")
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</MDBCol>
				</MDBRow>
			</div>

			<div className="manage-container m-4">
				<h4 className="header-text">
					{MANAGE_ACCOUNT_DATA.associated_companies}
				</h4>
				{assignedRoleList?.includes("ER_CLIENT") ||
				assignedRoleList?.includes("ER_USER") ? (
					<div className="mt-4">
						<CustomTable
							columnsData={tableHeader}
							renderTableData={assignedWCNList}
							customProps={{
								showExportButton: false,
								enableSort: true,
								isLoading: loading,
								showSwitch: true,
							}}
						/>
					</div>
				) : (
					!loading && (
						<div className="mt-4">
							<div class="card">
								<div class="card-body">
									<div class="row col-lg-12">
										All WCNs are assigned to this user.
									</div>
								</div>
							</div>
						</div>
					)
				)}
			</div>
		</>
	);
};

export default ManageAccountPage;
